<script setup>
import apiManager from '@/api';
import {
  ref,
  computed,
  watch,
  onMounted,
} from 'vue';
import { useToast } from 'vue-toastification';
import { faPlus, faMinus } from '@fortawesome/pro-solid-svg-icons';
import FormInput from '../../components/form/FormInput.vue';
import FormTextArea from '../../components/form/FormTextArea.vue';
import FormSelect from '../../components/form/FormSelect.vue';
import FordButton from '../../components/FordButton.vue';
import { formatDate } from '../../utils/format';
import { formatWeekDay, formatTime, formatTime2 } from '../../utils/date';

const props = defineProps({
  data: Object,
  eventId: String,
});

const emit = defineEmits(['form-submitted']);

const toast = useToast();

const loading = ref(false);

const SMEInfos = ref(props.data.userAppointmentSessionSMEs);

const statusSME = ref(props.data.status.id);

const editStatus = ref(false);

const removeSmeIds = ref([]);

function addSME() {
  if (SMEInfos.value.length + 1 <= 5) {
    SMEInfos.value = [...SMEInfos.value, {
      id: '',
      firstName: '',
      lastName: '',
      comment: '',
    }];
  }
}

function removeSME(index, type, id) {
  if (id) {
    removeSmeIds.value = [...removeSmeIds.value, { smeId: id }];
  }

  if (SMEInfos.value.length !== 0) {
    type.splice(index, 1);
  }
}
const deleteSME = (id, smeId) => apiManager.appointments.deleteSMESession(id, smeId);

const postSME = (id, payload) => apiManager.appointments.postSession(id, payload);

const editSME = (id, smeId, payload) => apiManager.appointments.patchSession(id, smeId, payload);

async function editStatusSME(eventId, smeId, payload) {
  const { data } = await apiManager.appointments.getAppointment(eventId)
    .then((response) => response)
    .catch((error) => error);

  const [apId] = data.filter((event) => event.sessions.some((session) => session.id === smeId));

  const result = await apiManager.appointments.patchAppointmentStatus(apId.id, smeId, payload)
    .then((response) => response)
    .catch((error) => error);

  return result;
}

const generateRemovals = (dataId) => removeSmeIds.value.map(async ({ smeId }) => {
  try {
    await deleteSME(dataId, smeId);
    removeSmeIds.value = removeSmeIds.value.filter((x) => x.smeId !== smeId);
  } catch (e) {
    throw new Error(e);
  }
});

const generateEdits = (dataId) => Promise.all(
  SMEInfos.value.map(async ({
    id, firstName, lastName, comment,
  }) => {
    if (!id
        && (firstName || lastName || comment)) { // insert SME
      const payload = {
        firstName,
        lastName,
        comment,
      };
      const postResult = await postSME(dataId, payload);

      return {
        id: postResult.data,
        firstName: payload.firstName,
        lastName: payload.lastName,
        comment: payload.comment,
      };
    }
    if (id) { // update SME
      const payload = {
        firstName,
        lastName,
        comment,
      };
      await editSME(dataId, id, payload);

      return {
        id,
        firstName,
        lastName,
        comment,
      };
    }
    return {
      id: '',
      firstName: '',
      lastName: '',
      comment: '',
    };
  }),
).then((resp) => {
  SMEInfos.value = resp;
});

async function formSubmit() {
  loading.value = true;
  try {
    if (editStatus.value === true) {
      const status = {
        statusId: statusSME.value,
      };
      await editStatusSME(props.eventId, props.data.id, status);
    }

    const removals = generateRemovals(props.data.id);

    const edits = generateEdits(props.data.id);

    const responses = await Promise.allSettled([...removals, edits]);
    if (responses.some((r) => r.status === 'rejected')) throw new Error(responses.filter((r) => r.status === 'rejected'));
    toast.success('The appointment details have been saved', { timeout: 10000 });
  } catch (e) {
    toast.error('There was an error updating some or all of the appointment information', { timeout: 10000 });
  } finally {
    loading.value = false;
    emit('form-submitted');
  }
}

const arrayStatus = computed(() => [
  { text: 'Scheduled', value: '29890cd1-267a-4f89-8066-31260f88607e' },
  { text: 'Completed', value: 'fe443a06-e3de-40c1-bfb9-265d0563c1b6' },
  { text: 'No Show', value: '4e3caaed-6539-4524-b513-e668a7b09c86' },
]);

const phone = props.data.user.phone.replace(/\D/gi, '').substring(0, 10);

const formatPhone = computed(() => {
  const number = phone.replace(/(\d{3})(\d{3})(\d{4})/gi, '($1) $2-$3');
  return number;
});

watch(statusSME, () => {
  editStatus.value = true;
});

watch(SMEInfos, () => {
  if (SMEInfos.value.length === 0) {
    SMEInfos.value.push({
      id: '',
      firstName: '',
      lastName: '',
      comment: '',
    });
  }
});

onMounted(() => {
  if (statusSME.value === 'f22011c3-2ae6-4e47-9736-f6d23db3bf5d') {
    statusSME.value = '29890cd1-267a-4f89-8066-31260f88607e';
  }

  if (SMEInfos.value.length === 0) {
    SMEInfos.value.push({
      id: '',
      firstName: '',
      lastName: '',
      comment: '',
    });
  }
  SMEInfos.value = props.data.userAppointmentSessionSMEs;
});
</script>
<template>
  <div class="container mx-auto w-full desktop:w-[800px] px-8 py-8 desktop:py-16">
    <div
      class="
        flex
        flex-col
        justify-center
        desktop:flex-row
        desktop:justify-between
    ">
      <div class="w-full">
        <h2
          class="
            text-[#4D4D4D]
            desktop:text-black
            text-base
            font-FordAntennaBold
            mb-3
        ">
          Customer Name
        </h2>
        <p
          class="
            text-lg
            desktop:text-2xl
            font-FordAntennaBold
            desktop:font-FordAntennaSemiBold
        ">
            {{ props.data.user.firstName }} {{  props.data.user.lastName }}
        </p>
        <p>{{ props.data.user.companyName }}</p>
        <p>{{ formatPhone }}</p>
        <p>{{ props.data.user.email }}</p>
      </div>
      <div class="w-full mt-6 desktop:mt-0">
        <h2
          class="
            text-[#4D4D4D]
            desktop:text-black
            text-base
            font-FordAntennaBold
            mb-3
        ">
          Appointment Time
        </h2>
        <p
          class="
            text-lg
            desktop:text-2xl
            font-FordAntennaBold
            desktop:font-FordAntennaSemiBold
        ">
          {{ formatTime2(props.data.start) }} - {{ formatTime(props.data.end) }}
        </p>
        <p>{{ formatWeekDay(props.data.start) }}, {{ formatDate(props.data.start, false) }}</p>
      </div>
    </div>
    <div class="mt-6">
      <h2
        class="
          text-[#4D4D4D]
          desktop:text-black
          text-base
          font-FordAntennaBold
          mb-3
      ">
        Business Solutions of Interest
      </h2>
      <p class="text-black text-sm font-FordAntennaLight">
        {{ props.data.user.userProfileQuestion.interestedInFordProSolutions }}
      </p>
    </div>
    <div class="mt-6">
      <h2
        class="
          text-[#4D4D4D]
          desktop:text-black
          text-base
          font-FordAntennaBold
          mb-3
      ">
        Comments from Customer
      </h2>
      <p class="text-black text-sm font-FordAntennaLight">
        {{ props.data.comment }}
      </p>
    </div>
    <hr class="my-8 desktop:my-14 h-px bg-[#707070] border-0"/>
    <form @submit.prevent="">
      <div class="grid grid-cols-2 gap-10">
        <form-select
          class="col-span-2 tablet:col-span-1"
          aria-label="Appointment Status *"
          label="Appointment Status"
          :data="arrayStatus"
          v-model="statusSME"
          required
        />
      </div>
      <div v-for="(SMEInfo, index) in SMEInfos" :key="index">
        <div
          @click.prevent="removeSME(index, SMEInfos, SMEInfo.id)"
          @keydown.enter.prevent="removeSME(index, SMEInfos)"
          class="
            flex
            items-center
            pt-2
            pb-5
            cursor-pointer
          ">
          <span>
            <font-awesome-icon
              :icon="faMinus"
              color="#FFF"
              size="sm"
              class="bg-[#00095B] w-[14px] h-[14px] p-2 rounded-full"
            />
          </span>
          <p class="ml-3 mt-[-2px] font-FordAntennaBold text-black">
            SME {{ index + 1 }} Information
          </p>
        </div>
        <div class="flex flex-col tablet:flex-row tablet:gap-10">
          <form-input
            class="w-full"
            aria-label="First Name *"
            v-model="SMEInfo.firstName"
            label="First Name"
          />
          <form-input
            class="w-full"
            aria-label="Last Name *"
            v-model="SMEInfo.lastName"
            label="Last Name"
          />
        </div>
        <div>
          <form-text-area
            class="question"
            rows="5"
            label="Comments"
            v-model="SMEInfo.comment"
          />
        </div>
      </div>
      <div
        @click.prevent="addSME"
        @keydown.enter.prevent="addSME"
        v-if="SMEInfos.length < 5"
        class="
          flex
          items-center
          cursor-pointer
        ">
        <span>
          <font-awesome-icon
            :icon="faPlus"
            color="#FFF"
            size="sm"
            class="bg-[#00095B] w-[14px] h-[14px] p-2 rounded-full"
          />
        </span>
        <p class="ml-3 mt-[-2px] font-FordAntennaBold text-black">Add Additional SME</p>
      </div>
      <div class="flex tablet:justify-center">
        <ford-button @click="formSubmit()" class="mt-[35px]">
          <img v-if="loading"
            :src="require('@/assets/img/spinner.svg')" alt="spinner"
          />
          <span v-else>Save Engagement</span>
        </ford-button>
      </div>
    </form>
  </div>
</template>
